import xhrService from '../api';
export type TextStyle = 'normal' | 'bold' | 'italic';

export interface PdfLayoutResponse {
  layout: PdfLayout | null;
  groups: PdfLayoutGroup[];
  logo_guid: string | null;
}

export interface HeaderConfig {
  enabled: boolean;
  mode: 'logo' | 'text' | 'logo_and_text';
  text?: string;
  repeat?: 'first' | 'all';
  justifyContent?: 'left' | 'center' | 'right';
  isJustifySeperate?: boolean;
  textStyle?: {
    justifyContent?: 'left' | 'center' | 'right';
    backgroundColor?: string;
  };
  logoJustifyContent?: 'left' | 'center' | 'right';
  logoStyle?: {
    width?: string;
    height?: string;
  }
  border?: string;
}

export interface FooterConfig {
  enabled: boolean;
  text?: string;
  justifyContent?: 'left' | 'center' | 'right';
  repeat?: 'first' | 'all';
}

export interface PlacementConfig {
  placement: 'default' | 'custom';
  after_group_id?: number | null;
}

export interface HeaderStyleConfig {
  fontSize?: string;
  fontStyle?: TextStyle;
  fontWeight?: number;
  color?: string;
  backgroundColor?: string;
}

// SW2-2344: the question groups' Appearance / text / padding options, for the
// Findings Log and Certification. Unset = today's print (full grid, stylesheet text).
export type SectionAppearance = 'border' | 'underline' | 'none';

export interface BodyStyleConfig {
  fontSize?: string;
  fontStyle?: TextStyle;
  fontWeight?: number | string;
  color?: string;
  // "<n>px": row height (Findings Log) or the gap between blocks (Certification)
  padding?: string;
}

export interface FindingsLogStyleConfig {
  header_style?: HeaderStyleConfig;
  body_style?: BodyStyleConfig;
  appearance?: SectionAppearance;
  split_open_closed?: boolean;
  columns?: {
    initiated_by?: boolean;
    closed_by?: boolean;
  };
}

export interface CertificationStyleConfig {
  header_style?: HeaderStyleConfig;
  body_style?: BodyStyleConfig;
  appearance?: SectionAppearance;
}

export interface StyleConfig {
  width?: string;
  name_style?: {
    fontSize?: string;
    fontStyle?: TextStyle;
    fontWeight?: number;
    color?: string;
    backgroundColor?: string;
  };
  description_style?: {
    fontSize?: string;
    fontStyle?: TextStyle;
    fontWeight?: number;
  };
  grid_style?: {
    widthPerColumn?: {
      [columnIndex: number]: string;
    },
    tableHeaderStyle?: {
      fontSize?: string;
      fontStyle?: TextStyle;
      fontWeight?: number;
      color?: string;
      backgroundColor?: string;
    };
  };
  question_style?: {
    padding?: string;
    fontSize?: string;
    fontStyle?: TextStyle;
    fontWeight?: number;
    color?: string;
    backgroundColor?: string;
  }
}

export interface PdfLayout {
  header_config?: HeaderConfig | null;
  footer_config?: FooterConfig | null;
  certifications_config: PlacementConfig;
  findings_config: PlacementConfig;
  findings_log_style_config?: FindingsLogStyleConfig | null;
  certification_style_config?: CertificationStyleConfig | null;
  show_borders: boolean;
  orientation: 'portrait' | 'landscape';
  page_number_position: 'top' | 'bottom' | 'none';
  margins: 'narrow' | 'normal' | 'wide';
  sections?: PdfLayoutSection[];
}

export interface PdfLayoutSection {
  question_group_id: number;
  question_appearance: 'border' | 'underline' | 'none';
  style_config: StyleConfig;
  page_break_after: boolean;
}

export type GroupLayoutType = 'FORM' | 'TABLE' | 'GRID';

export interface PdfLayoutGroup {
  id: number;
  name: string;
  description?: string | null;
  order: number;
  layout_type: GroupLayoutType;
  table_headers: string[];
  questions: PdfLayoutQuestion[];
}

export interface PdfLayoutQuestion {
  id: number;
  label: string;
  width: number; // percent of row width (e.g. 30, 70); defaults to 100
  sort_order: number;
  question_type_id: number;
  type_label: string;
  features: string[];
  can_comment: boolean;
}

export const pdfLayoutApi = {
  get: (templateId: number): Promise<PdfLayoutResponse> =>
    xhrService
      .get(`/api/inspection-templates/${templateId}/pdf-layout`)
      .then((res) => res.data),

  save: (templateId: number, data: PdfLayout): Promise<PdfLayout> =>
    xhrService
      .put(`/api/inspection-templates/${templateId}/pdf-layout`, data)
      .then((res) => res.data),
};

export default pdfLayoutApi;
